import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Plane, User, LogOut, Heart, History, BookOpen, Luggage, Sun, Moon, Monitor, Dices, Compass, Menu } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const enableTransition = () => {
  document.documentElement.classList.add("theme-transition");
  setTimeout(() => document.documentElement.classList.remove("theme-transition"), 350);
};

const NavBar = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  const switchTheme = (t: string) => {
    enableTransition();
    setTheme(t);
  };

  const ThemeIcon = theme === "dark" ? Moon : theme === "light" ? Sun : Monitor;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b border-border" aria-label="Main navigation">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-primary font-display font-bold text-base sm:text-lg hover:opacity-80 transition-opacity min-w-0 shrink"
        >
          <Plane className="h-5 w-5 shrink-0" />
          <span className="truncate">Decide My Destination</span>
        </button>

        {/* Mobile: compact menu */}
        <div className="flex md:hidden items-center gap-1 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open navigation menu"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/guides")} className="gap-2">
                <BookOpen className="h-4 w-4" /> Guides
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/packing-list")} className="gap-2">
                <Luggage className="h-4 w-4" /> Packing List
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/travel-bingo")} className="gap-2">
                <Dices className="h-4 w-4" /> Travel Bingo
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/travel-quiz")} className="gap-2">
                <Compass className="h-4 w-4" /> Travel Quiz
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => switchTheme("light")} className="gap-2">
                <Sun className="h-4 w-4" /> Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchTheme("dark")} className="gap-2">
                <Moon className="h-4 w-4" /> Dark
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchTheme("system")} className="gap-2">
                <Monitor className="h-4 w-4" /> System
              </DropdownMenuItem>
              {user && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/favorites")} className="gap-2">
                    <Heart className="h-4 w-4" /> Favorites
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/history")} className="gap-2">
                    <History className="h-4 w-4" /> Past Searches
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={signOut} className="gap-2 text-destructive">
                    <LogOut className="h-4 w-4" /> Sign Out
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {!user && (
            <Button variant="outline" size="sm" onClick={() => navigate("/auth")} className="gap-1.5 px-2.5">
              <User className="h-4 w-4" />
              <span className="text-sm">Sign In</span>
            </Button>
          )}
        </div>

        {/* Desktop / tablet: full nav */}
        <div className="hidden md:flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/guides")}
            aria-label="Guides"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <BookOpen className="h-4 w-4" />
            <span>Guides</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/packing-list")}
            aria-label="Packing List"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Luggage className="h-4 w-4" />
            <span>Packing List</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/travel-bingo")}
            aria-label="Travel Bingo"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Dices className="h-4 w-4" />
            <span>Bingo</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/travel-quiz")}
            aria-label="Travel Quiz"
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <Compass className="h-4 w-4" />
            <span>Quiz</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Change theme"
                className="h-9 w-9 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ThemeIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => switchTheme("light")} className="gap-2">
                <Sun className="h-4 w-4" /> Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchTheme("dark")} className="gap-2">
                <Moon className="h-4 w-4" /> Dark
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchTheme("system")} className="gap-2">
                <Monitor className="h-4 w-4" /> System
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <User className="h-4 w-4" />
                  <span className="text-sm truncate max-w-[150px]">
                    {user.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate("/favorites")} className="gap-2">
                  <Heart className="h-4 w-4" /> Favorites
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/history")} className="gap-2">
                  <History className="h-4 w-4" /> Past Searches
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="gap-2 text-destructive">
                  <LogOut className="h-4 w-4" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" size="sm" onClick={() => navigate("/auth")} className="gap-2">
              <User className="h-4 w-4" />
              Sign In
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
